import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { AxiosResponse } from 'axios';
import { useSearchParams } from 'react-router-dom';
import axiosInstant from '../../../api';
import { getBrandLogoUrl } from '../../../utils/brandLogo';
import { SingleFilter } from '../../admin/videos/adminVideosV2/singleFilter';
import {
  getFilterProps,
  ISingleOption,
  YES_ICON,
  NO_ICON,
  LINK_ICON,
} from '../../admin/videos/adminVideosV2/helper';
import { ProductCategoryFilter } from '../../admin/videos/adminVideosV2/productCategoryFilter';
import { ExpertFilter } from '../../admin/videos/adminVideosV2/expertFilter';
import {
  FilterPanel,
  FilterCountBadge,
  FilterTriggerIcon,
  FILTER_TRIGGER_CLASS,
} from '../../../components/FilterPanel';
import { countActiveFilters, PRODUCT_FILTER_KEYS } from '../../../utils';
import {
  IconSales,
  IconStory,
  IconChatMsgs,
  IconLink,
} from '../../../utils/icons';

const VIDEO_PRESENCE_OPTIONS: Array<ISingleOption> = [
  { id: 'true', name: 'Has videos', icon: YES_ICON },
  { id: 'false', name: 'No videos', icon: NO_ICON },
];

const PDP_PRESENCE_OPTIONS: Array<ISingleOption> = [
  { id: 'true', name: 'Has PDP URL', icon: LINK_ICON },
  { id: 'false', name: 'No PDP URL', icon: NO_ICON },
];

const CONSUMER_VOICE_OPTIONS: Array<ISingleOption> = [
  { id: 'scored', name: 'Scored', icon: YES_ICON },
  { id: 'none', name: 'Not scored', icon: NO_ICON },
];

interface BrandRow {
  id: number;
  name: string;
  logo?: string | null;
  cover_image?: string | null;
}

interface Props {
  onSearchUpdate: (params: any) => void;
  shouldShowFilter?: boolean;
}

export function ProductFilters({ onSearchUpdate, shouldShowFilter }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [brands, setBrands] = useState<Array<BrandRow>>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const activeFiltersCount = useMemo(
    () => countActiveFilters(searchParams, PRODUCT_FILTER_KEYS),
    [searchParams]
  );

  useEffect(() => {
    onSearchUpdate(Object.fromEntries(searchParams));
  }, [searchParams]);

  useEffect(() => {
    if (!shouldShowFilter) return;
    axiosInstant
      .get('/brands?limit=1000&sort=name')
      .then((response: AxiosResponse) => setBrands(response.data.rows || []))
      .catch(() => {});
  }, [shouldShowFilter]);

  const toOptions = (rows: Array<BrandRow>): Array<ISingleOption> =>
    rows.map((b) => ({ id: String(b.id), name: b.name, image: getBrandLogoUrl(b) }));

  const allBrandOptions = useMemo(
    () => [{ id: 'no_brand', name: 'No Brand' }, ...toOptions(brands)],
    [brands]
  );

  const onFilterSelect = (key: string, id?: string) => {
    setSearchParams((params) => {
      params.delete('page');
      if (!id) params.delete(key);
      else params.set(key, id);
      return params;
    });
  };

  const clearFilters = () => {
    setSearchParams((params) => {
      PRODUCT_FILTER_KEYS.forEach((key) => params.delete(key));
      params.delete('page');
      return params;
    });
  };

  if (!shouldShowFilter) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className={FILTER_TRIGGER_CLASS}
        style={{ fontSize: 13 }}
      >
        {FilterTriggerIcon}
        More filters
        <FilterCountBadge count={activeFiltersCount} />
      </button>

      <FilterPanel
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClearAll={clearFilters}
        activeCount={activeFiltersCount}
      >
        <SingleFilter
          {...getFilterProps(allBrandOptions, 'brand', 'Brand')}
          updateSearch={onFilterSelect}
          icon={IconSales}
          thumbnails
        />
        <ProductCategoryFilter onFilterSelect={onFilterSelect} />
        <ExpertFilter onFilterSelect={onFilterSelect} />
        <SingleFilter
          {...getFilterProps(VIDEO_PRESENCE_OPTIONS, 'videos', 'Videos')}
          updateSearch={onFilterSelect}
          icon={IconStory}
        />
        <SingleFilter
          {...getFilterProps(PDP_PRESENCE_OPTIONS, 'hasPdp', 'PDP URL')}
          updateSearch={onFilterSelect}
          icon={IconLink}
        />
        <SingleFilter
          {...getFilterProps(CONSUMER_VOICE_OPTIONS, 'cv', 'Consumer Voice')}
          updateSearch={onFilterSelect}
          icon={IconChatMsgs}
        />
      </FilterPanel>
    </>
  );
}
